import React, { useEffect, useState } from "react";
import {
    ChevronLeft,
    ChevronRight,
    User,
    Users,
    GraduationCap,
    School,
    BookOpen,
    Atom,
    TestTube,
    Leaf,
    Calculator,
    Sprout,
    TrendingUp,
    Cross,
    Plus,
    Globe,
    Users as UsersIcon,
    Monitor,
    Heart,
    Briefcase,
    Palette,
    PenTool,
    Home,
    Shield,
    Microscope,
    TreePine,
    Target,
    BarChart3,
    Book,
    Activity,
    Gamepad2,
    Zap,
    FileText,
    Building2,
    Coffee,
    Save,
    RefreshCw
} from "lucide-react";
import { API_BASE_URL } from "../../config/apiConfig";
import bg from "../../assets/first_bg.png";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";


const UpdateAcademicInfoPage = () => {
    const [currentStep, setCurrentStep] = useState(1);
    const [isTransitioning, setIsTransitioning] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [subjectsByClass, setSubjectsByClass] = useState({});
    const [showDropdown, setShowDropdown] = useState(false);
    const [filtered, setFiltered] = useState([]);
    const [userData, setUserData] = useState(null);

    // Subject icons mapping
    const subjectIcons = {
        "Physics": Atom,
        "Chemistry": TestTube,
        "Biology": Leaf,
        "Further Mathematics": Calculator,
        "Mathematics": Calculator,
        "Agricultural Science": Sprout,
        "Commerce": TrendingUp,
        "CRS": Cross,
        "Christian Religious Studies (CRS)": Cross,
        "English Language": PenTool,
        "English Studies": Book,
        "Basic Science": Microscope,
        "Basic Technology": Monitor,
        "Social Studies": Globe,
        "Civic Education": UsersIcon,
        "Computer Science": Monitor,
        "Physical and Health Education": Heart,
        "Physical and Health Education (PHE)": Heart,
        "Business Studies": Briefcase,
        "Creative Arts": Palette,
        "Home Economics": Home,
        "Security Education": Shield,
        "Literature": BookOpen,
        "Geography": Globe,
        "History": FileText,
        "Economics": BarChart3,
        "Government": Building2,
        "French": Coffee,
        "Arabic": Book,
        "Hausa": Book,
        "Igbo": Book,
        "Yoruba": Book,
        "Fine Arts": Palette,
        "Music": Activity,
        "Technical Drawing": Target,
        "Auto Mechanics": Gamepad2,
        "Electronics": Zap,
        "Building Construction": Building2,
        "Metal Work": Plus,
        "Wood Work": TreePine,
        "Food and Nutrition": Home,
        "Clothing and Textiles": Home
    };

    // Get icon for subject (fallback to BookOpen if not found)
    const getSubjectIcon = (subject) => {
        const normalized = subject.trim().toLowerCase();

        const foundKey = Object.keys(subjectIcons).find(key =>
            key.toLowerCase() === normalized ||
            key.toLowerCase().startsWith(normalized)
        );

        return subjectIcons[foundKey] || BookOpen;
    };



    const [formData, setFormData] = useState({
        accountType: "",
        school: "",
        yearGroup: "",
        guardianEmail: "",
        subjects: []
    });

    const [schools, setSchools] = useState([]);
    const [query, setQuery] = useState("");


    useEffect(() => {
        // Fetch Nigerian Secondary Schools from our own API (backed by the schools table)
        fetch(`${API_BASE_URL}/api/schools`)
            .then((res) => {
                if (!res.ok) {
                    throw new Error(`Network response was not ok: ${res.status}`);
                }
                return res.json();
            })
            .then((data) => {
                setSchools(Array.from(new Set(data.schools || [])).sort());
            })

            .catch((err) => {
                console.error("Failed to fetch secondary schools:", err);

                // Fallback to a static list of secondary schools
                setSchools([
                    "Government Secondary School, Kano",
                    "Federal Government College School",
                    "Capital Science Secondary School",
                    "Greenfield International Secondary School",
                    "Chrisland Senior School",
                    "Corona Secondary School",
                    "Lead British International Secondary School",
                    "British International School (Secondary)",
                    "Nigerian Turkish International Secondary School",
                    "Grange School Secondary Section",
                    "Faith Academy Secondary",
                    "Other",
                ]);
            });
    }, []);


    const handleChange = (e) => {
        const value = e.target.value;
        setQuery(value);

        if (value.length > 0) {
            const results = schools.filter((s) =>
                s.toLowerCase().includes(value.toLowerCase())
            );
            setFiltered(results);
            setShowDropdown(true);
        } else {
            setFiltered([]);
            setShowDropdown(false);
        }

        // keep formData in sync
        handleInputChange({ target: { name: "school", value } });
    };

    const handleSelect = (school) => {
        setQuery(school);
        setShowDropdown(false);

        handleInputChange({ target: { name: "school", value: school } });
    };


    // Fetch user data and the subjects-by-class list on component mount
    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true);
            await Promise.all([fetchUserData(), fetchQuestions()]);
            setIsLoading(false);
        };

        fetchData();
    }, []);

    const fetchUserData = async () => {
        try {
            const token = localStorage.getItem("token");
            if (!token) {
                console.error("No token found. User might not be logged in.");
                return;
            }

            const res = await fetch(`${API_BASE_URL}/api/users/me`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`,
                },
            });

            if (!res.ok) {
                console.error("Failed to fetch user data", await res.json());
                return;
            }

            const user = await res.json();
            setUserData(user);

            // Parse subjects if they're stored as JSON string
            let userSubjects = [];
            if (user.subjects) {
                try {
                    userSubjects = typeof user.subjects === 'string'
                        ? JSON.parse(user.subjects)
                        : user.subjects;
                } catch (e) {
                    console.error("Error parsing subjects:", e);
                    userSubjects = [];
                }
            }

            // Pre-populate form data with existing user data
            setFormData({
                accountType: user.accountType || "",
                school: user.school || "",
                yearGroup: user.yearGroup || "",
                guardianEmail: user.guardianEmail || "",
                subjects: userSubjects
            });
            setQuery(user.school || "");

        } catch (error) {
            console.error("Error fetching user data:", error);
        }
    };

    const fetchQuestions = async () => {
        try {
            // Grouped server-side and not gated on individual question
            // review_status, so a subject's presence here is stable — see
            // the same fix in onboarding/index.js for the full rationale.
            const response = await fetch(`${API_BASE_URL}/api/quiz/metadata`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${localStorage.getItem("token")}`
                },
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Failed to fetch subjects");
            }

            const data = await response.json();
            setSubjectsByClass(data.subjectsByClass || {});
        } catch (error) {
            console.error("Error fetching subjects by class:", error.message);
        }
    };

    const accountTypes = [
        { id: "student", label: "Student", icon: User },
        { id: "parent", label: "Parent", icon: Users },
        { id: "teacher", label: "Teacher", icon: GraduationCap }
    ];

    const preferredOrder = ["SS3", "SS2", "SS1", "JSS3", "JSS2", "JSS1"];
    const yearGroups = Object.keys(subjectsByClass);
    const sortedYearGroups = [...yearGroups].sort(
        (a, b) => preferredOrder.indexOf(a) - preferredOrder.indexOf(b)
    );

    const filteredSubjects = formData.yearGroup
        ? subjectsByClass[formData.yearGroup] || []
        : [];

    const handleAccountTypeSelect = (type) => {
        setFormData({ ...formData, accountType: type });
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleSubjectToggle = (subject) => {
        const updatedSubjects = formData.subjects.includes(subject)
            ? formData.subjects.filter(s => s !== subject)
            : [...formData.subjects, subject];
        setFormData({ ...formData, subjects: updatedSubjects });
    };

    const nextStep = () => {
        if (currentStep < 3 && !isTransitioning) {
            setIsTransitioning(true);
            setTimeout(() => {
                setCurrentStep(currentStep + 1);
                setIsTransitioning(false);
            }, 300);
        }
    };

    const prevStep = () => {
        if (currentStep > 1 && !isTransitioning) {
            setIsTransitioning(true);
            setTimeout(() => {
                setCurrentStep(currentStep - 1);
                setIsTransitioning(false);
            }, 300);
        }
    };

    const handleSubmit = async () => {
        setIsSaving(true);
        try {
            const token = localStorage.getItem("token");
            if (!token) {
                console.error("No token found. User might not be logged in.");
                return;
            }

            // Remove empty fields from formData before sending
            const payload = Object.fromEntries(
                Object.entries(formData).filter(
                    ([_, value]) => value && value.toString().trim() !== ""
                )
            );

            const res = await fetch(`${API_BASE_URL}/api/users/details`, {
                method: "POST", // Backend treats this as partial update
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`,
                },
                body: JSON.stringify(payload),
            });

            if (!res.ok) {
                const errorData = await res.json();
                console.error("Failed to update user details:", errorData);
                alert("Failed to update your information. Please try again.");
                return;
            }

            const data = await res.json();
            console.log("User details updated successfully:", data);
            toast.success("Your academic information has been updated successfully!")


        } catch (error) {
            console.error("Error updating user details:", error);
            alert("An error occurred while updating your information. Please try again.");
        } finally {
            setIsSaving(false);
        }
    };

    const canProceedStep1 = formData.accountType !== "";
    const canProceedStep2 = formData.yearGroup !== "";
    const canProceedStep3 = formData.subjects.length > 0;
    const navigate = useNavigate()
    console.log(filteredSubjects)

    if (isLoading) {
        return (
            <div className="flex justify-center items-center min-h-screen bg-gradient-to-br from-blue-600 to-blue-800">
                <div className="text-center text-white">
                    <RefreshCw className="w-12 h-12 animate-spin mx-auto mb-4" />
                    <p className="text-lg">Loading your information...</p>
                </div>
            </div>
        );
    }

    return (
        <div
            className="flex justify-center items-center min-h-screen py-4"
            style={{
                backgroundImage: `url(${bg})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
                backgroundColor: '#2148C0'
            }}>

            {/* CSS for animations */}
            <style jsx>{`
                .step-content {
                    transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
                }
                
                .step-entering {
                    opacity: 0;
                    transform: translateX(30px) scale(0.95);
                }
                
                .step-active {
                    opacity: 1;
                    transform: translateX(0) scale(1);
                }
                
                .step-exiting {
                    opacity: 0;
                    transform: translateX(-30px) scale(0.95);
                }
                
                .progress-step {
                    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
                    transform-origin: center;
                }
                
                .progress-step.active {
                    transform: scale(1.1);
                }
                
                .progress-line {
                    transition: all 0.6s cubic-bezier(0.4, 0, 0.2, 1);
                }
                
                .button-hover {
                    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                    transform-origin: center;
                }
                
                .button-hover:hover:not(:disabled) {
                    transform: scale(1.02);
                }
                
                .account-type-card {
                    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
                    transform-origin: center;
                }
                
                .account-type-card:hover {
                    transform: translateY(-4px) scale(1.02);
                    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
                }
                
                .year-group-btn {
                    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                    transform-origin: center;
                }
                
                .year-group-btn:hover {
                    transform: scale(1.05);
                }
                
                .subject-btn {
                    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                    transform-origin: center;
                    min-height: 80px;
                }
                
                .subject-btn:hover {
                    transform: scale(1.03);
                    box-shadow: 0 4px 15px rgba(255, 255, 255, 0.2);
                }
                
                .form-container {
                    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
                }
                
                .subjects-scroll {
                    scrollbar-width: thin;
                    scrollbar-color: rgba(255, 255, 255, 0.3) transparent;
                }
                
                .subjects-scroll::-webkit-scrollbar {
                    width: 6px;
                }
                
                .subjects-scroll::-webkit-scrollbar-track {
                    background: rgba(255, 255, 255, 0.1);
                    border-radius: 3px;
                }
                
                .subjects-scroll::-webkit-scrollbar-thumb {
                    background: rgba(255, 255, 255, 0.3);
                    border-radius: 3px;
                }
                
                .subjects-scroll::-webkit-scrollbar-thumb:hover {
                    background: rgba(255, 255, 255, 0.5);
                }
                
                @keyframes fadeInUp {
                    from {
                        opacity: 0;
                        transform: translateY(20px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
                
                .fade-in-up {
                    animation: fadeInUp 0.6s cubic-bezier(0.4, 0, 0.2, 1);
                }

                @keyframes pulse {
                    0%, 100% {
                        transform: scale(1);
                    }
                    50% {
                        transform: scale(1.05);
                    }
                }

                .selected-subject {
                    animation: pulse 0.6s ease-in-out;
                }
            `}</style>

            <div className="flex flex-col bg-transparent rounded-lg overflow-hidden w-[95%] max-w-4xl fade-in-up">
                {/* Header */}
                <div className="text-center mb-6 md:mb-8">
                    <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">
                        Update Academic Info
                    </h2>
                    <p className="text-white/80 text-sm md:text-base">
                        Keep your account type, school, and subjects up to date
                    </p>
                    {userData && (
                        <div className="mt-2 text-white/70 text-sm">
                            Welcome back, {userData.firstName} {userData.lastName}
                        </div>
                    )}

                    <div className="flex justify-center items-center mt-4 md:mt-6 space-x-2 md:space-x-4">
                        {[1, 2, 3].map((step) => (
                            <div key={step} className="flex items-center">
                                <div className={`progress-step w-6 h-6 md:w-8 md:h-8 rounded-full flex items-center justify-center text-xs md:text-sm font-bold ${currentStep >= step ? 'bg-white text-[#2148C0] active' : 'bg-white/30 text-white'}`}>
                                    {step}
                                </div>
                                {step < 3 && (
                                    <div className={`progress-line w-8 md:w-12 h-1 mx-1 md:mx-2 ${currentStep > step ? 'bg-white' : 'bg-white/30'}`} />
                                )}
                            </div>
                        ))}
                    </div>
                </div>

                {/* Step Content */}
                <div className="form-container bg-white/10 backdrop-blur-sm rounded-lg p-4 md:p-8 border border-white/20">
                    <div className={`step-content ${isTransitioning ? 'step-exiting' : 'step-active'}`}>

                        {/* Step 1: Account Type */}
                        {currentStep === 1 && (
                            <div className="space-y-6">
                                <h3 className="text-lg md:text-xl font-semibold text-white text-center mb-6">
                                    Account Type
                                </h3>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    {accountTypes.map(({ id, label, icon: Icon }) => (
                                        <button
                                            key={id}
                                            type="button"
                                            onClick={() => handleAccountTypeSelect(id)}
                                            className={`account-type-card p-4 md:p-6 rounded-lg border-2 ${formData.accountType === id ? 'border-white bg-white/20 text-white' : 'border-white/30 text-white/70 hover:border-white/50 hover:bg-white/10'}`}
                                        >
                                            <Icon className="w-10 h-10 md:w-12 md:h-12 mx-auto mb-3" />
                                            <div className="text-base md:text-lg font-medium">{label}</div>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Step 2: School & Year Group */}
                        {currentStep === 2 && (
                            <div className="space-y-4">
                                <h3 className="text-lg md:text-xl font-semibold text-white text-center mb-6">
                                    School & Year Group Details
                                </h3>

                                <div style={{ position: "relative", zIndex: 10 }}>
                                    <label className="block text-white mb-2 text-sm md:text-base">
                                        <School className="w-4 h-4 inline mr-2" />School
                                    </label>
                                    <div className="relative">
                                        <input
                                            type="text"
                                            value={query}
                                            onChange={handleChange}
                                            placeholder="Select your school or type the full name if not available"
                                            className="w-full p-3 rounded-lg bg-white/10 border border-white/30 text-white placeholder-white/50 focus:ring-2 focus:ring-white/50 transition-all duration-300 text-sm md:text-base"
                                        />
                                        {showDropdown && filtered.length > 0 && (
                                            <ul className="absolute left-0 right-0 bg-white text-gray-800 rounded-lg shadow-lg max-h-40 overflow-y-auto mt-1 z-50">
                                                {filtered.map((school) => (
                                                    <li key={school} onClick={() => handleSelect(school)} className="px-3 py-2 hover:bg-gray-200 cursor-pointer">
                                                        {school}
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                </div>

                                {formData.accountType === "student" && (
                                    <div>
                                        <label className="block text-white mb-2 text-sm md:text-base">Guardian Email</label>
                                        <input
                                            type="email"
                                            value={formData.guardianEmail || ""}
                                            onChange={(e) => setFormData(p => ({ ...p, guardianEmail: e.target.value }))}
                                            placeholder="Enter guardian's email"
                                            className="w-full p-3 rounded-lg bg-white/10 border border-white/30 text-white placeholder-white/50 focus:ring-2 focus:ring-white/50 transition-all duration-300 text-sm md:text-base"
                                        />
                                    </div>
                                )}

                                <div>
                                    <label className="block text-white mb-2 text-sm md:text-base">
                                        <GraduationCap className="w-4 h-4 inline mr-2" />Year Group
                                    </label>
                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                        {sortedYearGroups.map((year) => (
                                            <button
                                                key={year}
                                                type="button"
                                                onClick={() => setFormData({ ...formData, yearGroup: year, subjects: [] })}
                                                className={`p-3 rounded-lg border text-sm md:text-base transition-all duration-300 ${formData.yearGroup === year ? "border-white bg-white/20 text-white" : "border-white/30 text-white/70 hover:border-white/50"}`}
                                            >
                                                {year}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Step 3: Subject Selection */}
                        {currentStep === 3 && (
                            <div className="space-y-4 md:space-y-6">
                                <h3 className="text-lg md:text-xl font-semibold text-white text-center mb-4 md:mb-6">
                                    Update Your Subjects
                                </h3>
                                <div className="subjects-scroll grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-4 max-h-96 md:max-h-80 overflow-y-auto pr-2">
                                    {filteredSubjects.map((subject, index) => {
                                        const IconComponent = getSubjectIcon(subject);
                                        const isSelected = formData.subjects.includes(subject);

                                        return (
                                            <button
                                                key={subject}
                                                type="button"
                                                onClick={() => handleSubjectToggle(subject)}
                                                className={`subject-btn p-3 md:p-4 rounded-lg border flex flex-col items-center justify-center space-y-2 text-center ${isSelected
                                                    ? "border-white bg-white/20 text-white selected-subject"
                                                    : "border-white/30 text-white/70 hover:border-white/50 hover:bg-white/5"
                                                    }`}
                                                style={{
                                                    animationDelay: `${index * 50}ms`,
                                                    animation: `fadeInUp 0.6s cubic-bezier(0.4, 0, 0.2, 1) ${index * 50}ms both`,
                                                }}
                                            >
                                                <IconComponent
                                                    className={`w-6 h-6 md:w-8 md:h-8 ${isSelected ? "text-white" : "text-white/70"
                                                        }`}
                                                />
                                                <span
                                                    className={`text-xs md:text-sm font-medium leading-tight ${isSelected ? "text-white" : "text-white/80"
                                                        }`}
                                                >
                                                    {subject}
                                                </span>
                                                {isSelected && <div className="w-2 h-2 bg-white rounded-full"></div>}
                                            </button>
                                        );
                                    })}
                                </div>

                                <div className="bg-white/10 rounded-lg p-3 md:p-4 border border-white/20">
                                    <p className="text-white/90 text-sm md:text-base font-medium text-center">
                                        Selected: {formData.subjects.length} subject
                                        {formData.subjects.length !== 1 ? "s" : ""}
                                    </p>
                                    {formData.subjects.length > 0 && (
                                        <div className="mt-2 flex flex-wrap gap-1 md:gap-2 justify-center">
                                            {formData.subjects.slice(0, 3).map((subject, index) => (
                                                <span
                                                    key={index}
                                                    className="bg-white/20 text-white text-xs px-2 py-1 rounded-full"
                                                >
                                                    {subject.length > 15
                                                        ? `${subject.substring(0, 15)}...`
                                                        : subject}
                                                </span>
                                            ))}
                                            {formData.subjects.length > 3 && (
                                                <span className="bg-white/20 text-white text-xs px-2 py-1 rounded-full">
                                                    +{formData.subjects.length - 3} more
                                                </span>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Navigation */}
                    <div className="flex justify-between mt-6 md:mt-8 gap-4">
                        <button
                            onClick={prevStep}
                            disabled={currentStep === 1 || isTransitioning}
                            className={`button-hover flex items-center px-4 md:px-6 py-2 rounded-lg text-sm md:text-base ${currentStep === 1 || isTransitioning ? 'bg-white/20 text-white/50 cursor-not-allowed' : 'bg-white/30 text-white hover:bg-white/40'}`}
                        >
                            <ChevronLeft className="w-4 h-4 mr-1" /> Previous
                        </button>

                        {currentStep < 3 ? (
                            <button
                                onClick={nextStep}
                                disabled={isTransitioning || (currentStep === 1 && !canProceedStep1) || (currentStep === 2 && !canProceedStep2)}
                                className={`button-hover flex items-center px-4 md:px-6 py-2 rounded-lg text-sm md:text-base ${isTransitioning || ((currentStep === 1 && !canProceedStep1) || (currentStep === 2 && !canProceedStep2)) ? 'bg-white/20 text-white/50 cursor-not-allowed' : 'bg-white text-[#2148C0] hover:bg-gray-200'}`}
                            >
                                Next <ChevronRight className="w-4 h-4 ml-1" />
                            </button>
                        ) : (
                            <button
                                onClick={handleSubmit}
                                disabled={!canProceedStep3 || isSaving}
                                className={`button-hover flex items-center px-4 md:px-6 py-2 rounded-lg text-sm md:text-base ${!canProceedStep3 || isSaving
                                    ? "bg-white/20 text-white/50 cursor-not-allowed"
                                    : "bg-white text-blue-600 hover:bg-gray-200"
                                    }`}
                            >
                                {isSaving ? (
                                    <>
                                        <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                                        Saving...
                                    </>
                                ) : (
                                    <>
                                        <Save className="w-5 h-5 mr-2" />
                                        Save Changes
                                    </>
                                )}
                            </button>
                        )}
                    </div>
                </div>

                {/* Back to Dashboard Link */}
                <p className="mt-4 md:mt-6 text-center text-white/70 text-sm md:text-base">
                    <button
                        onClick={() => navigate(-1)}
                        className="text-white hover:underline font-medium"
                    >
                        ← Back
                    </button>
                </p>
            </div>

        </div>
    );
};

export default UpdateAcademicInfoPage;